import { useMemo } from 'react';

import { useAppConfig } from '@/hooks/useAppConfig';
import { useUserContext } from '@/modules/auth/hooks/useUser';

import { useP_UserWorkspacesQuery } from '../graphql/queries/generated/p_UserWorkspaces.productivity';
import { composePersonId, type SwitcherWorkspace } from '../utils';

/** The signed-in person's Workeffy workspaces, and the one every request currently runs under. */
const useUserWorkspaces = () => {
  const { id: userId, currentSubWorkspace } = useUserContext();
  const {
    workspaceId,
    ui: { isUnifiedSidebar },
  } = useAppConfig();

  const personId = workspaceId && userId ? composePersonId(workspaceId, userId) : undefined;

  // The productivity useQuery re-runs its effect whenever the variables object changes, so a fresh
  // object every render loops forever (re-fetching, or re-setting state while paused).
  const variables = useMemo(() => ({ personId: personId ?? '' }), [personId]);

  // Workspaces live in Workeffy, which only unified sidebar tenants are on. Elsewhere the request
  // fails and the client keeps retrying it, so skip it and show no switcher.
  const [{ data, fetching }] = useP_UserWorkspacesQuery({
    pause: !personId || !isUnifiedSidebar,
    requestPolicy: 'cache-and-network',
    variables,
  });

  const workspaces = useMemo<SwitcherWorkspace[]>(
    () =>
      !isUnifiedSidebar
        ? []
        : (data?.userWorkspaces.workspaces ?? []).flatMap((workspace) =>
            workspace.entityId
              ? [
                  {
                    id: workspace.entityId,
                    workeffyId: workspace.id,
                    name: workspace.displayName ?? workspace.entityId,
                  },
                ]
              : [],
          ),
    [data, isUnifiedSidebar],
  );

  // The token's sub-workspace is what every request runs under; lastWorkspaceId covers a token
  // minted before sub-workspaces existed.
  const currentId = currentSubWorkspace ?? data?.userWorkspaces.lastWorkspaceId ?? undefined;
  const currentWorkspace = workspaces.find(({ id }) => id === currentId);

  return { workspaces, currentWorkspace, personId, isLoading: fetching && !data };
};

export default useUserWorkspaces;
